/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { ZCDSelect } from '@zencity/common-ui';
import { SelectOption } from '@zencity/common-ui/lib/ZCD/ZCDFilter/types';
import { LoaderMask } from 'components/LoaderMask/LoaderMask';
import React, { useContext, useMemo } from 'react';
import { WidgetGeneratorContext } from 'screens/WidgetGenerator/contexts/WidgetGeneratorContext';
import { getSatisfactionBarChartData } from 'screens/WidgetGenerator/helpers/questionsScore';
import {
  AspectValue,
  MaintainAndFocusDataType,
  MaintainAndFocusServicesFiltersData,
  OverallSatisfactionScoreQuestion,
} from 'screens/WidgetGenerator/utils/misc';
import { Spacer } from 'components/Spacer/Spacer';
import { logger } from 'utils/logger';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'customHooks/hooks';
import { FieldTitle } from 'screens/WidgetGenerator/components/FieldTitle/FieldTitle';
import { ReportFilterProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';

const formatSatisfactionDataToOptions = (options?: OverallSatisfactionScoreQuestion[]): SelectOption[] => {
  if (!options) return [];
  return options.map((option) => ({
    value: option.genericQuestionId,
    label: option.text,
  }));
};

const EMPTY_MAINTAIN_AND_FOCUS_VALUES: MaintainAndFocusServicesFiltersData = {
  [MaintainAndFocusDataType.MAINTAIN]: [],
  [MaintainAndFocusDataType.FOCUS]: [],
};

export const MaintainAndFocusFilters: React.FC<ReportFilterProps<MaintainAndFocusServicesFiltersData>> = (
  props: ReportFilterProps<MaintainAndFocusServicesFiltersData>,
) => {
  const { value: filterValue, onChange } = props;
  const { client, cycles, selectedDateRange, selectedSurveyGroup } = useContext(WidgetGeneratorContext);
  const { t: translate } = useTranslation();

  const maintainAndFocusCurrentValues = filterValue || EMPTY_MAINTAIN_AND_FOCUS_VALUES;

  const fetchMaintainAndFocusData = async () => {
    const fetchedSatisfactionData = await getSatisfactionBarChartData({
      client,
      cycles,
      selectedDateRange,
      selectedSurveyGroupId: selectedSurveyGroup.id,
    });

    return fetchedSatisfactionData.filter((question) => question.aspectValue === AspectValue.CHARACTERISTIC);
  };

  const {
    fetchedData = [],
    isLoading,
    error,
  } = useAsyncEffect(fetchMaintainAndFocusData, [client, cycles, selectedDateRange]);
  if (error) {
    logger.error(error);
  }

  const handleChange = (newValues: SelectOption[], keyToUpdate: MaintainAndFocusDataType) => {
    const selectedData = newValues.map((option) => option.value as number);
    onChange({
      ...maintainAndFocusCurrentValues,
      [keyToUpdate]: selectedData,
    });
  };

  const options = useMemo(() => formatSatisfactionDataToOptions(fetchedData), [fetchedData]);
  const maintainFilteredValues = options.filter((option) => filterValue?.maintain.includes(option.value as number));
  const focusFilteredValues = options.filter((option) => filterValue?.focus.includes(option.value as number));

  return (
    <LoaderMask isLoading={isLoading}>
      <FieldTitle title={translate('widgetGenerator.widgets.maintainAndFocus.maintain.selectionTitle')} />
      <ZCDSelect
        isDisabled={!options.length}
        isMulti
        options={options}
        value={maintainFilteredValues}
        onChange={(newValue) => handleChange(newValue as SelectOption[], MaintainAndFocusDataType.MAINTAIN)}
      />
      <Spacer orientation="vertical" size="xsmall" />
      <FieldTitle title={translate('widgetGenerator.widgets.maintainAndFocus.focus.selectionTitle')} />
      <ZCDSelect
        isDisabled={!options.length}
        isMulti
        options={options}
        value={focusFilteredValues}
        onChange={(newValue) => handleChange(newValue as SelectOption[], MaintainAndFocusDataType.FOCUS)}
      />
      <Spacer orientation="vertical" size="xsmall" />
    </LoaderMask>
  );
};
